import React, {useState, useEffect} from 'react';
import withSSR from '../../withSSR';
import {getData} from '../../services/GetData';
import SearchSection from '../../organisms/SearchSection/SearchSection';
import './Home.scss';
import FestivalCarousel from '../../organisms/FestivalCarousel';
import {useTranslation} from 'react-i18next';
import SeoInterlinks from '../../atoms/Seo/SeoInterlink';
import SeoText from '../../atoms/Seo/SeoText';
import {GetSeoInfo} from '../../services/SeoService';
import {GtmInitialize, TrackSparePartsPageView} from '../../services/gtmEvents';
import MenuDataService from '../../services/MenuDataService';
import GeneralDataService from '../../services/GeneralDataService';

import Observer from '@researchgate/react-intersection-observer';
import {Helmet} from 'react-helmet';

import Header from '../../organisms/Header/Header';
import Footer from '../../organisms/Footer/Footer';

const Home = props => {
  const sparePartsInitialData = props && props.sparePartsInitial;
  const info = props && props.info;
  const {t} = useTranslation();
  const baseUrl = process.env.RAZZLE_BASE_URL || window.env.RAZZLE_BASE_URL;
  const sparePartsUrl = process.env.RAZZLE_SPARE_PARTS_URL || window.env.RAZZLE_SPARE_PARTS_URL;
  const cdnUrl = process.env.RAZZLE_CDN || window.env.RAZZLE_CDN;
  const partsUrl = process.env.RAZZLE_PARTS_URL || window.env.RAZZLE_PARTS_URL;
  const allAgroshopsUrl = process.env.RAZZLE_ALL_AGROSHOPS_URL || window.env.RAZZLE_ALL_AGROSHOPS_URL;
  const featuredAgroshopsUrl = process.env.RAZZLE_FEATURED_AGROSHOPS_URL || window.env.RAZZLE_FEATURED_AGROSHOPS_URL;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    LUX.init();
    TrackSparePartsPageView(`${sparePartsInitialData.CategoryName}`);
    setMounted(true);
    setTimeout(function () {
      GtmInitialize();
    }, 3000);
  }, []);

  const componentsMapper = {
    'AgroshopSection': 'AgroshopSection',
    'BannerImg': 'BannerImg',
    'ReplacementCarousel': 'ReplacementCarousel',
    'AgroshopSectionRandom': 'AgroshopSectionRandom'
  };

  const handleChange = async event => {
    if (event.isIntersecting) {
      addView(componentsMapper[event.target.id], event.target.style);
    }
  };

  const [loadedComponents, setLoadedComponents] = useState({});
  const addView = async (id, targetStyle) => {
    if (id === 'AgroshopSection') {
      const loadedComponentData = {};
      import('../../molecules/' + id)
        .then(Component => {
          loadedComponentData[id] =
            <Component.default cdn={`${cdnUrl}media/catalog/agroshop/`} title={t('featured_agroshops')}
                               linkAllAgroshops={featuredAgroshopsUrl} IsCorporate="true" order="ordervalue"
                               CategoryId={sparePartsInitialData.CategoryId}
                               categoryName={sparePartsInitialData.CategoryName}/>;
          if (!loadedComponents[id]) {
            setLoadedComponents({...loadedComponents, ...loadedComponentData});
            targetStyle.minHeight = 'unset';
          }
        });
    } else if (id === 'BannerImg') {
      const loadedComponentData = {};
      import('../../atoms/' + id)
        .then(Component => {
          loadedComponentData[id] =
            <Component.default title={t('banner_tractor_title')} bajada={t('banner_tractor_text')}
                               vermas={t('see_more')} url={partsUrl}
                               imgUrl={`${cdnUrl}media/static/landings/repuestos/banner.jpg`}/>;
          if (!loadedComponents[id]) {
            setLoadedComponents({...loadedComponents, ...loadedComponentData});
            targetStyle.minHeight = 'unset';
          }
        });
    } else if (id === 'ReplacementCarousel') {
      const loadedComponentData = {};
      import('../../organisms/' + id)
        .then(Component => {
          loadedComponentData[id] = <Component.default categoryIds={sparePartsInitialData.CategoryIdsCarousel}
                                                       numberOfItems={sparePartsInitialData.NumberOfItems - 1}
                                                       homeMounted={mounted}/>;
          if (!loadedComponents[id]) {
            setLoadedComponents({...loadedComponents, ...loadedComponentData});
            targetStyle.minHeight = 'unset';
          }
        });
    } else if (id === 'AgroshopSectionRandom') {
      const loadedComponentData = {};
      import('../../molecules/AgroshopSection')
        .then(Component => {
          loadedComponentData[id] =
            <Component.default cdn={`${cdnUrl}media/catalog/agroshop/`} title={t('online_agroshops')}
                               linkAllAgroshops={allAgroshopsUrl} IsCorporate="false" order="random"
                               CategoryId={sparePartsInitialData.CategoryId}
                               categoryName={sparePartsInitialData.CategoryName}/>;
          if (!loadedComponents[id]) {
            setLoadedComponents({...loadedComponents, ...loadedComponentData});
            targetStyle.minHeight = 'unset';
          }
        });
    }
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>{t('head_title')}</title>
        <meta name="description" content={t('head_description')} />
        <link rel="canonical" href={`${baseUrl}${sparePartsUrl}`}/>
      </Helmet>
      <Header />
      <div className={'page-content'}>
        <SearchSection machinerySearch={sparePartsInitialData.MachinerySearch}
                       sparePartsSearch={sparePartsInitialData.SparePartsSearch}
                       categoryName={sparePartsInitialData.CategoryName}
                       cdnUrl={cdnUrl}/>

        {sparePartsInitialData.FestivalEditions.map((fc, index) =>
          <FestivalCarousel key={index} carousel={fc} homeMounted={mounted}/>
        )}
        {Object.keys(componentsMapper).map((Obs) => <Observer onChange={handleChange}
                                                              rootMargin='-50px 0px 0px 0px'><span
          style={{display: 'block', minHeight: '50vh', height: 'fit-content'}}
          id={Obs}>{loadedComponents[Obs]}</span></Observer>)}
        <div className="container">
          <SeoText title={GetSeoInfo().seoText.title} content={GetSeoInfo().seoText.content}/>
          <SeoInterlinks title={GetSeoInfo().interlinks.title} items={GetSeoInfo().interlinks.items}/>
        </div>
      </div>
      <Footer footerData={info.staticData} />
    </React.Fragment>
  );
};

// Server side rendering example
Home.getInitialProps = async ({match, req, res}) => {
  const apiGatewayUrl = process.env.RAZZLE_API_GATEWAY_URL || window.env.RAZZLE_API_GATEWAY_URL;
  try {
    const {data} = await getData({url: `${apiGatewayUrl}/category-home/spare-parts`});
    const menuData = await MenuDataService();
    const staticData = GeneralDataService();

    return {
      sparePartsInitial: data,
      info: {
        menuData: menuData.data,
        staticData
      }
    };

  } catch (e) {
    return console.error(e);
  }
};

export default withSSR(Home);
